import { onScopeDispose, ref } from 'vue'

export type LatestRequestTask<T> = (signal: AbortSignal) => Promise<T>

export class SupersededRequestError extends Error {
  constructor() {
    super('请求已被更新的请求替代')
    this.name = 'SupersededRequestError'
  }
}

export function isSupersededRequestError(
  error: unknown
): error is SupersededRequestError {
  return error instanceof SupersededRequestError
}

export function useLatestRequest() {
  const isPending = ref(false)
  let requestSequence = 0
  let activeController: AbortController | null = null

  const run = async <T>(task: LatestRequestTask<T>): Promise<T> => {
    const requestId = ++requestSequence
    activeController?.abort()

    const controller = new AbortController()
    activeController = controller
    isPending.value = true

    try {
      const result = await task(controller.signal)
      if (requestId !== requestSequence) {
        throw new SupersededRequestError()
      }
      return result
    } catch (error) {
      if (requestId !== requestSequence || controller.signal.aborted) {
        throw new SupersededRequestError()
      }
      throw error
    } finally {
      if (requestId === requestSequence) {
        activeController = null
        isPending.value = false
      }
    }
  }

  const cancel = () => {
    if (!activeController) return
    requestSequence += 1
    activeController.abort()
    activeController = null
    isPending.value = false
  }

  onScopeDispose(cancel)

  return {
    isPending,
    run,
    cancel
  }
}
